/**
 * React Query hooks for PF-4 (fieldiq-api#1227): the rate card, a rent-in's
 * billing rate, and field tickets.
 *
 * Same rules as usePipelineFacility.ts and usePfDigs.ts: every key starts
 * ['pipelineFacility'] and every write invalidates that one prefix (a line
 * moves the ticket, the register's total and the job's Tickets tab); every
 * read spreads LOAD_FAILURE_QUERY_OPTIONS so a refusal reads as a refusal,
 * never as an empty list.
 *
 * A write that can return a signed ticket to draft resolves to
 * `{ ticket, reverted }` so the screen can say so — `reverted_to_draft` is
 * never dropped on the way.
 */
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { unwrapArray } from '../lib/api-envelope';
import { LOAD_FAILURE_QUERY_OPTIONS } from '../lib/load-failure';
import { invalidatePf, unwrapPf, unwrapPfList } from './usePipelineFacility';
import type {
  PfRate,
  PfRateWrite,
  PfRentInBilling,
  PfTicket,
  PfTicketEdit,
  PfTicketHeaderWrite,
  PfTicketLineCreate,
  PfTicketLinePatch,
  PfTicketRecipient,
  PfTicketSendResult,
} from '../types/pf-tickets';

type Params = Record<string, string | undefined>;
const clean = (p?: Params) => {
  if (!p) return undefined;
  const out: Record<string, string> = {};
  Object.entries(p).forEach(([k, v]) => {
    if (v) out[k] = v;
  });
  return Object.keys(out).length ? out : undefined;
};

export const pfTicketKeys = {
  rates: (p?: Params) => ['pipelineFacility', 'rates', clean(p) ?? {}] as const,
  rate: (id: string) => ['pipelineFacility', 'rate', id] as const,
  rentInBilling: (id: string) => ['pipelineFacility', 'rentInBilling', id] as const,
  tickets: (p?: Params) => ['pipelineFacility', 'tickets', clean(p) ?? {}] as const,
  ticket: (id: string) => ['pipelineFacility', 'ticket', id] as const,
  recipient: (id: string) => ['pipelineFacility', 'ticketRecipient', id] as const,
  equipmentTypes: () => ['pipelineFacility', 'equipmentTypes'] as const,
};

function usePfWrite<V, R>(fn: (v: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: fn, onSuccess: () => invalidatePf(qc) });
}

/**
 * A delete: drop the deleted record's own query and do NOT wait on the refetch.
 * Awaiting invalidatePf here refetched the gone record (404, with retries) for
 * ~4s before the screen's onSuccess could navigate away — measured on PFT-0003.
 */
function usePfDelete(fn: (id: string) => Promise<unknown>, key: (id: string) => readonly unknown[]) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (_data, id) => {
      qc.removeQueries({ queryKey: key(id), exact: true });
      void invalidatePf(qc);
    },
  });
}

/** A ticket write's echo: the ticket as it now is, and whether it went back to draft. */
export interface PfTicketEditResult {
  ticket: PfTicket | null;
  reverted: boolean;
}
const editResult = (body: PfTicketEdit | unknown): PfTicketEditResult => ({
  ticket: unwrapPf<PfTicket>(body).data,
  reverted: !!(body && typeof body === 'object' && (body as { reverted_to_draft?: unknown }).reverted_to_draft === true),
});

// ─── Rate card ───────────────────────────────────────────────────────────────

export interface PfRateFilters extends Params {
  /** A customer's uuid, or 'default'. */
  customer_id?: string;
  kind?: string;
  on?: string;
}

export function usePfRates(filters?: PfRateFilters, enabled = true) {
  return useQuery({
    queryKey: pfTicketKeys.rates(filters),
    queryFn: async () => unwrapPfList<PfRate>((await api.pipelineFacility.rates.list(clean(filters))).data),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function usePfRate(id: string | undefined) {
  return useQuery({
    queryKey: pfTicketKeys.rate(id ?? ''),
    queryFn: async () => unwrapPf<PfRate>((await api.pipelineFacility.rates.get(id as string)).data),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export const useCreatePfRate = () =>
  usePfWrite(async (body: PfRateWrite) => unwrapPf<PfRate>((await api.pipelineFacility.rates.create(body)).data));
export const useUpdatePfRate = () =>
  usePfWrite(async ({ id, body }: { id: string; body: PfRateWrite }) =>
    unwrapPf<PfRate>((await api.pipelineFacility.rates.update(id, body)).data));
export const useDeletePfRate = () => usePfDelete(async (id: string) => (await api.pipelineFacility.rates.remove(id)).data, pfTicketKeys.rate);

/** The tenant's equipment types, for an owned-equipment rate. */
export function usePfEquipmentTypes(enabled = true) {
  return useQuery({
    queryKey: pfTicketKeys.equipmentTypes(),
    queryFn: async () =>
      unwrapArray<{ id: string; name?: string | null }>((await api.equipmentTypes.list()).data)
        .map((t) => ({ id: t.id, name: t.name || '—' }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

// ─── A rent-in's billing rate ────────────────────────────────────────────────

export function usePfRentInBillingRates(rentInIds: string[], enabled = true) {
  return useQueries({
    queries: rentInIds.map((id) => ({
      queryKey: pfTicketKeys.rentInBilling(id),
      queryFn: async () => unwrapPf<PfRentInBilling>((await api.pipelineFacility.rentInBilling.get(id)).data),
      enabled,
      ...LOAD_FAILURE_QUERY_OPTIONS,
    })),
  });
}

export const useSetPfRentInBillingRate = () =>
  usePfWrite(async ({ rentInId, rateId }: { rentInId: string; rateId: string | null }) =>
    unwrapPf<PfRentInBilling>((await api.pipelineFacility.rentInBilling.set(rentInId, rateId)).data));

// ─── Tickets ─────────────────────────────────────────────────────────────────

export interface PfTicketFilters extends Params {
  job_id?: string;
  customer_id?: string;
  status?: string;
}

export function usePfTickets(filters?: PfTicketFilters, enabled = true) {
  return useQuery({
    queryKey: pfTicketKeys.tickets(filters),
    queryFn: async () => unwrapPfList<PfTicket>((await api.pipelineFacility.tickets.list(clean(filters))).data),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function usePfTicket(id: string | undefined) {
  return useQuery({
    queryKey: pfTicketKeys.ticket(id ?? ''),
    queryFn: async () => unwrapPf<PfTicket>((await api.pipelineFacility.tickets.get(id as string)).data),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

/** Who a send would address — the office's write role only (the API gates it as a write). */
export function usePfTicketRecipient(id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: pfTicketKeys.recipient(id ?? ''),
    queryFn: async () => unwrapPf<PfTicketRecipient>((await api.pipelineFacility.tickets.recipient(id as string)).data),
    enabled: !!id && enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export const useCreatePfTicket = () =>
  usePfWrite(async ({ jobId, workDate }: { jobId: string; workDate?: string }) =>
    unwrapPf<PfTicket>((await api.pipelineFacility.tickets.create(jobId, workDate ? { work_date: workDate } : {})).data));

/** Refresh answers `refreshed.reverted` rather than `reverted_to_draft`; both reach the screen as `reverted`. */
export const useRefreshPfTicket = () =>
  usePfWrite(async (id: string): Promise<PfTicketEditResult> => {
    const body = (await api.pipelineFacility.tickets.refresh(id)).data as { refreshed?: { reverted?: unknown } } | undefined;
    return { ticket: unwrapPf<PfTicket>(body).data, reverted: body?.refreshed?.reverted === true };
  });

export const useUpdatePfTicket = () =>
  usePfWrite(async ({ id, body }: { id: string; body: PfTicketHeaderWrite }) =>
    editResult((await api.pipelineFacility.tickets.update(id, body)).data));

export const useDeletePfTicket = () => usePfDelete(async (id: string) => (await api.pipelineFacility.tickets.remove(id)).data, pfTicketKeys.ticket);

export const useAddPfTicketLine = () =>
  usePfWrite(async ({ id, body }: { id: string; body: PfTicketLineCreate }) =>
    editResult((await api.pipelineFacility.tickets.addLine(id, body)).data));

export const useUpdatePfTicketLine = () =>
  usePfWrite(async ({ id, lineId, body }: { id: string; lineId: string; body: PfTicketLinePatch }) =>
    editResult((await api.pipelineFacility.tickets.updateLine(id, lineId, body)).data));

export const useRemovePfTicketLine = () =>
  usePfWrite(async ({ id, lineId }: { id: string; lineId: string }) =>
    editResult((await api.pipelineFacility.tickets.removeLine(id, lineId)).data));

export const useOfficeApprovePfTicket = () =>
  usePfWrite(async (id: string) => unwrapPf<PfTicket>((await api.pipelineFacility.tickets.officeApprove(id)).data));

export const useSendPfTicket = () =>
  usePfWrite(async ({ id, email, cc }: { id: string; email?: string; cc?: string[] }): Promise<PfTicketSendResult> => {
    const body: { email?: string; cc?: string[] } = {};
    if (email) body.email = email;
    if (cc && cc.length) body.cc = cc;
    return (await api.pipelineFacility.tickets.send(id, body)).data;
  });

export const useRecordPfTicketApproval = () =>
  usePfWrite(async ({ id, form }: { id: string; form: FormData }) =>
    unwrapPf<PfTicket>((await api.pipelineFacility.tickets.recordApproval(id, form)).data));
